import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { reportsApi, accountsApi } from '../api/client.js';
import { formatAmount } from '../components/Layout.js';
import { SkeletonRows, TableEmpty, Spinner, ErrorBanner } from '../components/States.js';
import ExportMenu from '../components/ExportMenu.js';
import ScopeBar from '../components/ScopeBar.js';
import { roleCan, permHint } from '../utils/permissions.js';
import type { TrialBalanceRow, IncomeStatement, CashSummary, AccountLedger, AccountComparisonRow, Account, BalanceSheet, BalanceSheetRow, AgingReport, AgingGroup } from '../api/client.js';

type TabKey = 'trial' | 'income' | 'balance' | 'cash' | 'compare' | 'aging';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'trial', label: '科目余额表' },
  { key: 'income', label: '利润表' },
  { key: 'balance', label: '资产负债表' },
  { key: 'cash', label: '资金流量' },
  { key: 'compare', label: '科目对比账簿' },
  { key: 'aging', label: '账龄分析' },
];

const TAB_EXPORT_KIND: Record<Exclude<TabKey, 'compare' | 'aging'>, 'trial-balance' | 'income-statement' | 'balance-sheet' | 'cash-summary'> = {
  trial: 'trial-balance',
  income: 'income-statement',
  balance: 'balance-sheet',
  cash: 'cash-summary',
};

/** 账龄分档条颜色（与 ReportService.AGING_BUCKETS 顺序一致） */
const BUCKET_COLORS = ['bg-emerald-500', 'bg-amber-400', 'bg-orange-500', 'bg-red-500'];

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  asset: '资产',
  liability: '负债',
  equity: '权益',
  revenue: '收入',
  expense: '费用',
};

export default function Reports() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tabFromUrl = TABS.find((t) => t.key === params.get('tab'))?.key;
  const [tab, setTab] = useState<TabKey>(tabFromUrl || 'trial');
  const [period, setPeriod] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const switchTab = (next: TabKey) => {
    setTab(next);
    setParams(next === 'trial' ? {} : { tab: next }, { replace: true });
  };

  const [trial, setTrial] = useState<{ rows: TrialBalanceRow[]; sumDebit: number; sumCredit: number } | null>(null);
  const [income, setIncome] = useState<IncomeStatement | null>(null);
  const [balance, setBalance] = useState<BalanceSheet | null>(null);
  const [cash, setCash] = useState<CashSummary | null>(null);
  const [exporting, setExporting] = useState(false);
  const [drill, setDrill] = useState<{ code: string; name: string } | null>(null);

  // 科目对比账簿
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [compare, setCompare] = useState<{ period: string; rows: AccountComparisonRow[] } | null>(null);
  const [comparing, setComparing] = useState(false);

  // 账龄分析
  const [aging, setAging] = useState<AgingReport | null>(null);
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [termsDays, setTermsDays] = useState(30);
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState('');
  const [agingBusy, setAgingBusy] = useState(false);
  // 8.4 数据可见范围（admin/senior 可圈定；受限角色服务端强制 mine）
  const [scope, setScope] = useState('all');
  const [scopeInfo, setScopeInfo] = useState<{ mode: 'all' | 'mine' | 'owner'; limited: boolean } | null>(null);

  const exportCurrent = async () => {
    if (tab === 'compare' || tab === 'aging') return;
    setExporting(true);
    try {
      await reportsApi.exportXlsx(TAB_EXPORT_KIND[tab], period || undefined);
    } catch (e: any) {
      setError(e.message || '导出失败');
    } finally {
      setExporting(false);
    }
  };

  const runCompare = useCallback(async (codes: string[], p: string) => {
    if (codes.length === 0) { setCompare(null); return; }
    setComparing(true);
    setError('');
    try {
      setCompare(await reportsApi.comparison(codes, p || undefined));
    } catch (e: any) {
      setError(e.message || '对比查询失败');
    } finally {
      setComparing(false);
    }
  }, []);

  const toggleCode = (code: string) => {
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code].slice(0, 30)
    );
  };

  const exportCompare = async () => {
    if (selectedCodes.length === 0) return;
    setExporting(true);
    try {
      await reportsApi.exportComparison(selectedCodes, period || undefined);
    } catch (e: any) {
      setError(e.message || '导出失败');
    } finally {
      setExporting(false);
    }
  };

  const runAgingScan = async () => {
    setScanning(true);
    setScanMsg('');
    setError('');
    try {
      const r = await reportsApi.agingScan({ asOf, termsDays });
      setScanMsg(
        `账龄扫描完成：新增 ${r.created} 条、刷新 ${r.updated} 条、已结清自动销号 ${r.resolved} 条` +
        `（当前超期 ${r.overdueCount} 笔 ¥${formatAmount(r.overdueTotal)}），可到「企微监管 · 信号中心」处理`
      );
    } catch (e: any) {
      setError(e.message || '超期扫描失败');
    } finally {
      setScanning(false);
    }
  };

  const exportAgingXlsx = async () => {
    setAgingBusy(true);
    setError('');
    try {
      await reportsApi.exportAging({ asOf: asOf || undefined, termsDays, scope: scope !== 'all' ? scope : undefined });
    } catch (e: any) {
      setError(e.message || '账龄导出失败');
    } finally {
      setAgingBusy(false);
    }
  };

  const printAgingHtml = async () => {
    setError('');
    try {
      await reportsApi.printAging({ asOf: asOf || undefined, termsDays, scope: scope !== 'all' ? scope : undefined });
    } catch (e: any) {
      setError(e.message || '账龄套打失败');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const p = period || undefined;
      if (tab === 'trial') setTrial(await reportsApi.trialBalance(p));
      if (tab === 'income') setIncome(await reportsApi.incomeStatement(p));
      if (tab === 'balance') setBalance(await reportsApi.balanceSheet(p));
      if (tab === 'cash') setCash(await reportsApi.cashSummary(p));
      if (tab === 'aging') {
        const r = await reportsApi.aging({ asOf: asOf || undefined, termsDays, scope: scope !== 'all' ? scope : undefined });
        setAging(r);
        setScopeInfo(r.scopeInfo ?? null);
      }
      if (tab === 'compare') {
        const { accounts: accs } = await accountsApi.list({ pageSize: 500 });
        setAccounts(accs);
      }
    } catch (e: any) {
      setError(e.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [tab, period, runCompare, asOf, termsDays, scope]);

  useEffect(() => {
    load();
  }, [load]);

  // 对比 tab：选中科目或期间变化时刷新对比结果
  useEffect(() => {
    if (tab === 'compare') runCompare(selectedCodes, period);
  }, [tab, period, selectedCodes, runCompare]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-slate-800">报表中心</h2>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-500">期间</label>
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
          />
          {period && (
            <button
              onClick={() => setPeriod('')}
              className="btn btn-ghost btn-sm"
            >
              全部
            </button>
          )}
          {tab !== 'compare' && tab !== 'aging' && (
            <ExportMenu
              size="md"
              busy={exporting}
              items={[
                { label: '导出 Excel（xlsx）', icon: '📊', onSelect: exportCurrent, disabled: exporting },
              ]}
            />
          )}
        </div>
      </div>

      {/* Tab 切换 */}
      <div className="flex gap-1 bg-slate-200 rounded-lg p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => switchTab(t.key)}
            className={`px-4 py-1.5 rounded-md text-sm transition-colors ${
              tab === t.key ? 'bg-white text-slate-800 shadow' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}
      {loading && (
        <div className="py-6 text-center"><Spinner /></div>
      )}

      {/* 科目余额表 */}
      {tab === 'trial' && trial && !loading && (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-4 py-3">科目编码</th>
                <th className="px-4 py-3">科目名称</th>
                <th className="px-4 py-3">类别</th>
                <th className="px-4 py-3 text-right">本期借方</th>
                <th className="px-4 py-3 text-right">本期贷方</th>
                <th className="px-4 py-3 text-right">余额（借-贷）</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {trial.rows.map((r) => (
                <tr key={r.accountCode} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono">
                    <button
                      onClick={() => setDrill({ code: r.accountCode, name: r.accountName })}
                      className="text-blue-600 hover:underline"
                      title="查看明细账"
                    >
                      {r.accountCode} ↗
                    </button>
                  </td>
                  <td className="px-4 py-2">{r.accountName}</td>
                  <td className="px-4 py-2">{ACCOUNT_TYPE_LABELS[r.accountType] || r.accountType || '-'}</td>
                  <td className="px-4 py-2 text-right font-mono">{formatAmount(r.totalDebit)}</td>
                  <td className="px-4 py-2 text-right font-mono">{formatAmount(r.totalCredit)}</td>
                  <td className={`px-4 py-2 text-right font-mono ${r.balance < 0 ? 'text-red-600' : ''}`}>
                    {formatAmount(r.balance)}
                  </td>
                </tr>
              ))}
              {trial.rows.length === 0 && (
                <TableEmpty
                  colSpan={6}
                  icon="📊"
                  title="该期间暂无已过账凭证"
                  hint="报表仅统计已过账 / 已审核凭证，可先去凭证页过账，或切换上方期间"
                />
              )}
            </tbody>
            <tfoot className="bg-slate-50 font-medium">
              <tr>
                <td colSpan={3} className="px-4 py-3">合计（试算平衡：借贷应相等）</td>
                <td className="px-4 py-3 text-right font-mono">{formatAmount(trial.sumDebit)}</td>
                <td className="px-4 py-3 text-right font-mono">{formatAmount(trial.sumCredit)}</td>
                <td
                  className={`px-4 py-3 text-right ${
                    Math.abs(trial.sumDebit - trial.sumCredit) < 0.005 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {Math.abs(trial.sumDebit - trial.sumCredit) < 0.005 ? '✔ 平衡' : '✘ 不平衡'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* 利润表 */}
      {tab === 'income' && income && !loading && (
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow p-5 md:col-span-2 space-y-4">
            <div>
              <h3 className="font-medium text-slate-700 mb-2">收入</h3>
              {income.revenues.length === 0 && <p className="text-sm text-slate-400">暂无收入记录</p>}
              {income.revenues.map((r) => (
                <div key={r.accountCode} className="flex justify-between text-sm py-1 border-b border-dashed">
                  <span>{r.accountCode} {r.accountName}</span>
                  <span className="font-mono">{formatAmount(r.amount)}</span>
                </div>
              ))}
            </div>
            <div>
              <h3 className="font-medium text-slate-700 mb-2">费用</h3>
              {income.expenses.length === 0 && <p className="text-sm text-slate-400">暂无费用记录</p>}
              {income.expenses.map((r) => (
                <div key={r.accountCode} className="flex justify-between text-sm py-1 border-b border-dashed">
                  <span>{r.accountCode} {r.accountName}</span>
                  <span className="font-mono">{formatAmount(r.amount)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <StatCard label="收入合计" value={income.totalRevenue} color="text-green-600" />
            <StatCard label="费用合计" value={income.totalExpense} color="text-orange-600" />
            <StatCard
              label="净利润"
              value={income.netProfit}
              color={income.netProfit >= 0 ? 'text-blue-600' : 'text-red-600'}
            />
          </div>
        </div>
      )}

      {/* 资产负债表 */}
      {tab === 'balance' && balance && !loading && (
        <div className="grid md:grid-cols-2 gap-4 items-start">
          {/* 资产侧 */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-medium text-slate-700">资产</h3>
              <span className="text-xs text-slate-400">截至：{balance.period}</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="px-4 py-2">科目</th>
                  {period && <th className="px-4 py-2 text-right">期初余额</th>}
                  <th className="px-4 py-2 text-right">期末余额</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <BsRows rows={balance.assets} showOpening={!!period} onDrill={setDrill} />
                {balance.unclassified.map((r) => (
                  <BsRows key={r.accountCode} rows={[{ ...r, accountName: `${r.accountName}（未分类）` }]} showOpening={!!period} onDrill={setDrill} />
                ))}
                {balance.assets.length === 0 && balance.unclassified.length === 0 && (
                  <TableEmpty colSpan={period ? 3 : 2} icon="🏦" title="暂无资产类科目余额" hint="录入现金/银行等资产科目的已过账凭证后展示" />
                )}
              </tbody>
              <tfoot className="bg-slate-50 font-medium">
                <tr>
                  <td className="px-4 py-3">资产合计</td>
                  {period && (
                    <td className="px-4 py-3 text-right font-mono">
                      {formatAmount([...balance.assets, ...balance.unclassified].reduce((s, r) => s + r.openingBalance, 0))}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right font-mono">{formatAmount(balance.totalAssets)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* 负债 + 权益侧 */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="px-4 py-3 border-b"><h3 className="font-medium text-slate-700">负债</h3></div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="px-4 py-2">科目</th>
                    {period && <th className="px-4 py-2 text-right">期初余额</th>}
                    <th className="px-4 py-2 text-right">期末余额</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <BsRows rows={balance.liabilities} showOpening={!!period} onDrill={setDrill} />
                  {balance.liabilities.length === 0 && (
                    <TableEmpty colSpan={period ? 3 : 2} icon="📋" title="暂无负债类科目余额" hint="应付/预收等负债科目过账后展示" />
                  )}
                </tbody>
                <tfoot className="bg-slate-50 font-medium">
                  <tr>
                    <td className="px-4 py-3">负债合计</td>
                    {period && <td className="px-4 py-3 text-right font-mono">{formatAmount(balance.liabilities.reduce((s, r) => s + r.openingBalance, 0))}</td>}
                    <td className="px-4 py-3 text-right font-mono">{formatAmount(balance.totalLiabilities)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="px-4 py-3 border-b"><h3 className="font-medium text-slate-700">权益</h3></div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="px-4 py-2">项目</th>
                    {period && <th className="px-4 py-2 text-right">期初余额</th>}
                    <th className="px-4 py-2 text-right">期末余额</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {balance.equity.map((r) =>
                    r.accountCode === '—'
                      ? (
                        <tr key={r.accountName} className="bg-amber-50/40">
                          <td className="px-4 py-2 text-slate-600">{r.accountName}</td>
                          {period && <td className="px-4 py-2 text-right font-mono text-slate-500">{formatAmount(r.openingBalance)}</td>}
                          <td className={`px-4 py-2 text-right font-mono ${r.closingBalance < 0 ? 'text-red-600' : ''}`}>{formatAmount(r.closingBalance)}</td>
                        </tr>
                      )
                      : <BsRows key={r.accountCode} rows={[r]} showOpening={!!period} onDrill={setDrill} />
                  )}
                </tbody>
                <tfoot className="bg-slate-50 font-medium">
                  <tr>
                    <td className="px-4 py-3">权益合计（含累计利润）</td>
                    {period && <td className="px-4 py-3 text-right font-mono">{formatAmount(balance.equity.reduce((s, r) => s + r.openingBalance, 0))}</td>}
                    <td className="px-4 py-3 text-right font-mono">{formatAmount(balance.totalEquity)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* 平衡校验 */}
            <div className="bg-white rounded-xl shadow px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-slate-600">负债和权益总计</span>
              <div className="flex items-center gap-3">
                <span className="text-sm font-mono font-semibold">{formatAmount(balance.totalLiabilities + balance.totalEquity)}</span>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    balance.balanced ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}
                  title={balance.balanced ? '资产 = 负债 + 权益' : '存在差额，检查科目类型设置与未分类科目'}
                >
                  {balance.balanced ? '✔ 平衡' : `✘ 差额 ${formatAmount(balance.difference)}`}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 资金流量 */}
      {tab === 'cash' && cash && !loading && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <StatCard label="资金净变动（凭证口径）" value={cash.totalNetChange} color={cash.totalNetChange >= 0 ? 'text-green-600' : 'text-red-600'} />
            <StatCard label="微信实际收款流入" value={cash.wechatInflow} color="text-blue-600" />
            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-xs text-slate-400 mb-1">期间</p>
              <p className="text-lg font-semibold text-slate-700">{cash.period}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="px-4 py-3">科目</th>
                  <th className="px-4 py-3 text-right">流入（借方）</th>
                  <th className="px-4 py-3 text-right">流出（贷方）</th>
                  <th className="px-4 py-3 text-right">净变动</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {cash.accounts.map((r) => (
                  <tr key={r.accountCode} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <button
                        onClick={() => setDrill({ code: r.accountCode, name: r.accountName })}
                        className="text-blue-600 hover:underline"
                        title="查看明细账"
                      >
                        {r.accountCode} {r.accountName} ↗
                      </button>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{formatAmount(r.inflow)}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatAmount(r.outflow)}</td>
                    <td className={`px-4 py-2 text-right font-mono ${r.netChange < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatAmount(r.netChange)}
                    </td>
                  </tr>
                ))}
                {cash.accounts.length === 0 && (
                  <TableEmpty
                    colSpan={4}
                    icon="💰"
                    title="暂无资金类科目记录"
                    hint="资金流量仅统计现金 / 银行等资金类科目的已过账分录"
                  />
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 多科目对比账簿 */}
      {tab === 'compare' && !loading && (
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-slate-700">选择科目</h3>
              {selectedCodes.length > 0 && (
                <button onClick={() => setSelectedCodes([])} className="text-xs text-slate-400 hover:text-slate-600">
                  清空({selectedCodes.length})
                </button>
              )}
            </div>
            <div className="max-h-[60vh] overflow-auto divide-y">
              {accounts.map((a) => (
                <label key={a.code} className="flex items-center gap-2 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={selectedCodes.includes(a.code)} onChange={() => toggleCode(a.code)} />
                  <span className="font-mono text-xs text-slate-500">{a.code}</span>
                  <span className="truncate">{a.name}</span>
                  <span className="ml-auto text-xs text-slate-400">{ACCOUNT_TYPE_LABELS[a.type] || a.type}</span>
                </label>
              ))}
              {accounts.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">暂无科目</p>}
            </div>
          </div>

          <div className="md:col-span-2 bg-white rounded-xl shadow overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-medium text-slate-700">
                对比结果 <span className="text-xs font-normal text-slate-400">（期间：{compare?.period || period || '全部'}）</span>
              </h3>
              <ExportMenu
                busy={exporting}
                items={[
                  { label: '导出对比账簿（xlsx）', icon: '📊', onSelect: exportCompare, disabled: exporting || selectedCodes.length === 0, hint: '选中至少一个科目后可导出' },
                ]}
              />
            </div>
            {comparing ? (
              <div className="py-8 text-center"><Spinner /></div>
            ) : selectedCodes.length === 0 ? (
              <div className="px-4 py-10 text-center animate-fade-in-up">
                <div className="w-12 h-12 rounded-2xl bg-brand-50 grid place-items-center text-2xl mx-auto">🧮</div>
                <p className="text-sm font-medium text-slate-600 mt-1">选择要对比的科目</p>
                <p className="text-xs text-slate-400">从左侧勾选科目进行对比（最多 30 个）</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="px-4 py-2">科目</th>
                    <th className="px-4 py-2 text-right">期初余额</th>
                    <th className="px-4 py-2 text-right">本期借方</th>
                    <th className="px-4 py-2 text-right">本期贷方</th>
                    <th className="px-4 py-2 text-right">期末余额</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(compare?.rows || []).map((r) => (
                    <tr key={r.accountCode} className="hover:bg-slate-50">
                      <td className="px-4 py-2">
                        <button onClick={() => setDrill({ code: r.accountCode, name: r.accountName })} className="text-blue-600 hover:underline font-mono text-xs">{r.accountCode}</button>
                        <span className="ml-2">{r.accountName}</span>
                      </td>
                      <td className={`px-4 py-2 text-right font-mono ${r.openingBalance < 0 ? 'text-red-600' : ''}`}>{formatAmount(r.openingBalance)}</td>
                      <td className="px-4 py-2 text-right font-mono">{formatAmount(r.totalDebit)}</td>
                      <td className="px-4 py-2 text-right font-mono">{formatAmount(r.totalCredit)}</td>
                      <td className={`px-4 py-2 text-right font-mono ${r.closingBalance < 0 ? 'text-red-600' : ''}`}>{formatAmount(r.closingBalance)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 font-medium">
                  <tr>
                    <td className="px-4 py-3">合计</td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3 text-right font-mono">{formatAmount((compare?.rows || []).reduce((s, r) => s + r.totalDebit, 0))}</td>
                    <td className="px-4 py-3 text-right font-mono">{formatAmount((compare?.rows || []).reduce((s, r) => s + r.totalCredit, 0))}</td>
                    <td className="px-4 py-3"></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      )}

      {/* 应收 / 应付账龄分析 */}
      {tab === 'aging' && aging && !loading && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow px-4 py-3">
            <ScopeBar value={scope} onChange={setScope} scopeInfo={scopeInfo} />
          </div>
          <div className="bg-white rounded-xl shadow px-4 py-3 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-500" htmlFor="aging-asof">截止日</label>
              <input
                id="aging-asof"
                type="date"
                value={asOf}
                onChange={(e) => setAsOf(e.target.value)}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-500" htmlFor="aging-terms">信用期（天）</label>
              <input
                id="aging-terms"
                type="number"
                min={0}
                max={365}
                value={termsDays}
                onChange={(e) => setTermsDays(Math.max(0, Math.min(365, Number(e.target.value) || 0)))}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm w-24"
              />
            </div>
            <span className="text-xs text-slate-400">
              往来科目未结余额按 FIFO 逐笔核销，账龄自凭证记账日起算（仅统计已过账 / 已审核）
            </span>
            <ExportMenu
              busy={agingBusy}
              items={[
                { label: '导出账龄表（xlsx）', icon: '📊', onSelect: exportAgingXlsx, disabled: agingBusy },
                { label: '套打账龄表（A4）', icon: '🖨', onSelect: printAgingHtml, hint: '携 token 拉取打印版 HTML 新窗口自动唤起打印' },
              ]}
            />
            <button
              onClick={runAgingScan}
              disabled={scanning || !roleCan('aging:scan')}
              title={permHint('aging:scan')}
              className="btn btn-primary btn-sm ml-auto"
            >
              {scanning ? '扫描中…' : '📣 扫描超期并推送预警'}
            </button>
          </div>

          {scanMsg && (
            <div className="p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm flex items-center justify-between">
              <span>{scanMsg}</span>
              <button onClick={() => setScanMsg('')} className="text-blue-400 hover:text-blue-600">×</button>
            </div>
          )}

          <div className="grid md:grid-cols-4 gap-4">
            {(['receivable', 'payable'] as const).map((side) => {
              const g = aging.groups.find((x) => x.side === side);
              return <AgingStatCard key={side} group={g} />;
            })}
            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-xs text-slate-400 mb-1">超期合计（两侧）</p>
              <p className="text-2xl font-bold font-mono text-red-600">
                ¥{formatAmount(aging.groups.reduce((s, g) => s + g.overdueAmount, 0))}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {aging.groups.reduce((s, g) => s + g.overdueCount, 0)} 笔超信用期 {termsDays} 天
              </p>
            </div>
            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-xs text-slate-400 mb-1">最长账龄</p>
              <p className="text-2xl font-bold font-mono text-slate-700">
                {Math.max(0, ...aging.groups.map((g) => g.maxDays))} <span className="text-sm font-medium">天</span>
              </p>
              <p className="text-xs text-slate-400 mt-1">参与科目 {aging.accounts.length} 个·截至 {aging.asOf}</p>
            </div>
          </div>

          {aging.groups.every((g) => g.count === 0) ? (
            <div className="bg-white rounded-xl shadow px-4 py-12 text-center animate-fade-in-up">
              <div className="w-12 h-12 rounded-2xl bg-brand-50 grid place-items-center text-2xl mx-auto">🧾</div>
              <p className="text-sm font-medium text-slate-600 mt-1">当前口径下无未结往来款项</p>
              <p className="text-xs text-slate-400">往来挂账与回款/付款过账后自动入表；也可将截止日往后调</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-4 items-start">
              {aging.groups.filter((g) => g.count > 0).map((g) => (
                <AgingGroupCard key={g.side} group={g} onDrill={setDrill} onCpDrill={(texts) => navigate(`/journal?${texts.map((t) => `counterparty=${encodeURIComponent(t)}`).join('&')}`)} />
              ))}
            </div>
          )}

          {aging.accounts.length > 0 && (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="px-4 py-3 border-b"><h3 className="font-medium text-slate-700">参与往来的科目</h3></div>
              <div className="flex flex-wrap gap-2 px-4 py-3">
                {aging.accounts.map((a) => (
                  <button
                    key={a.code}
                    onClick={() => setDrill({ code: a.code, name: a.name })}
                    title="查看该科目明细账"
                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm hover:border-brand-300 hover:bg-brand-50 transition-colors"
                  >
                    <span className="font-mono text-xs text-slate-500">{a.code}</span> {a.name}
                    <span className={`ml-2 font-mono ${a.open < 0 ? 'text-red-600' : 'text-slate-600'}`}>
                      ¥{formatAmount(a.open)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {drill && (
        <LedgerModal code={drill.code} name={drill.name} period={period} onClose={() => setDrill(null)} />
      )}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white rounded-xl shadow p-5">
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      <p className={`text-2xl font-bold font-mono ${color}`}>{formatAmount(value)}</p>
    </div>
  );
}

/** 账龄汇总卡（应收 / 应付） */
function AgingStatCard({ group }: { group?: AgingGroup }) {
  if (!group) return <StatCard label="账龄" value={0} color="text-slate-400" />;
  const isRec = group.side === 'receivable';
  return (
    <div className="bg-white rounded-xl shadow p-5">
      <p className="text-xs text-slate-400 mb-1">{isRec ? '应收未结（资产侧）' : '应付未付（负债侧）'}</p>
      <p className={`text-2xl font-bold font-mono ${isRec ? 'text-blue-600' : 'text-violet-600'}`}>
        ¥{formatAmount(group.total)}
      </p>
      <p className="text-xs text-slate-400 mt-1">
        {group.count} 笔·均 {group.avgDays} 天
        {group.overdueCount > 0 && (
          <span className="ml-1 text-red-600">·超期 {group.overdueCount} 笔</span>
        )}
      </p>
    </div>
  );
}

/** 账龄分组卡：分档占比条 + 未结项明细；对手方 chip 可点击穿透到凭证列表 */
function AgingGroupCard({ group, onDrill, onCpDrill }: {
  group: AgingGroup;
  onDrill: (d: { code: string; name: string }) => void;
  onCpDrill?: (texts: string[]) => void;
}) {
  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-medium text-slate-700">
          {group.label}
          <span className="ml-2 text-xs font-normal text-slate-400">{group.count} 笔未结</span>
        </h3>
        <span
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            group.overdueCount > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
          }`}
        >
          {group.overdueCount > 0
            ? `超期 ${group.overdueCount} 笔 ¥${formatAmount(group.overdueAmount)}`
            : '无超期'}
        </span>
      </div>

      {/* 分档占比条 */}
      <div className="px-4 pt-3">
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden flex">
          {group.buckets.map((b, i) => (
            <div
              key={b.label}
              className={BUCKET_COLORS[i % BUCKET_COLORS.length]}
              style={{ width: `${(b.ratio * 100).toFixed(2)}%` }}
              title={`${b.label}：¥${formatAmount(b.amount)}（${b.count} 笔）`}
            />
          ))}
        </div>
        <div className="grid grid-cols-4 gap-1 mt-2">
          {group.buckets.map((b, i) => (
            <div key={b.label} className="text-center">
              <div className="flex items-center justify-center gap-1">
                <span className={`w-1.5 h-1.5 rounded-full ${BUCKET_COLORS[i % BUCKET_COLORS.length]}`} />
                <span className="text-[11px] text-slate-500 truncate">{b.label}</span>
              </div>
              <div className="text-xs font-mono text-slate-700">{formatAmount(b.amount)}</div>
              <div className="text-[10px] text-slate-400">{(b.ratio * 100).toFixed(0)}% · {b.count} 笔</div>
            </div>
          ))}
        </div>
      </div>

      {group.byCounterparty.length > 0 && (
        <div className="px-4 pt-3">
          <div className="text-[11px] text-slate-400 mb-1.5">按对手方汇总（未结金额降序）</div>
          <div className="flex flex-wrap gap-1.5">
            {group.byCounterparty.map((c) => {
              const aliasTexts = (c.texts || []).filter((t) => t !== c.counterparty); // 同行异名文本（现名可能不在 texts 内）
              return (
              <button
                key={c.counterparty}
                disabled={c.counterparty === '（未指定）' || !onCpDrill}
                onClick={() => onCpDrill?.(c.texts?.length ? c.texts : [c.counterparty])}
                title={`未结 ${formatAmount(c.amount)} · ${c.count} 笔 · 超期 ${c.overdueCount} 笔 ${formatAmount(c.overdueAmount)} · 最长 ${c.maxDays} 天${c.contact || c.phone ? ` · 档案联系人：${[c.contact, c.phone].filter(Boolean).join(' ')}` : ''}${aliasTexts.length ? ` · 含曾用名文本：${aliasTexts.join('、')}` : ''}${c.counterparty !== '（未指定）' ? ' · 点击查看该对手方凭证' : ''}`}
                className={`text-xs px-2 py-1 rounded-lg border text-left ${
                  c.overdueCount > 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-slate-50 border-slate-200 text-slate-600'
                } ${c.counterparty !== '（未指定）' ? 'cursor-pointer hover:ring-2 hover:ring-indigo-200' : 'cursor-default'}`}
              >
                {c.counterparty}{aliasTexts.length > 0 && <span className="ml-1 text-[10px] px-1 rounded bg-indigo-50 border border-indigo-200 text-indigo-600" title={`已按档案归户合并 ${aliasTexts.length} 个曾用名文本`}>+{aliasTexts.length}曾用名</span>} <span className="font-mono font-medium">{formatAmount(c.amount)}</span>
                <span className="text-[10px] text-slate-400 ml-1">{c.count}笔</span>
                {c.overdueCount > 0 && <span className="ml-1 text-[10px] px-1 rounded bg-red-100 text-red-700">超期{c.overdueCount}</span>}
                {(c.contact || c.phone) && <span className="ml-1 text-[10px] px-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-700" title={`档案联系人：${[c.contact, c.phone].filter(Boolean).join(' ')}`}>档</span>}
              </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="max-h-[46vh] overflow-auto mt-2">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left sticky top-0">
            <tr>
              <th className="px-4 py-2">凭证 / 摘要</th>
              <th className="px-4 py-2 text-right">未结金额</th>
              <th className="px-4 py-2 text-right">已核销</th>
              <th className="px-4 py-2 text-right">账龄</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {group.items.map((it) => (
              <tr key={it.journalId + it.accountCode} className="hover:bg-slate-50">
                <td className="px-4 py-2">
                  <button
                    onClick={() => onDrill({ code: it.accountCode, name: it.accountName })}
                    className="text-blue-600 hover:underline font-mono text-xs"
                    title="查看该科目明细账"
                  >
                    {it.reference} ↗
                  </button>
                  <div className="text-xs text-slate-500 truncate max-w-[240px]">{it.description || it.accountName}</div>
                  <div className="text-[11px] text-slate-400">{it.entryDate} · {it.accountCode} {it.accountName}</div>
                </td>
                <td className={`px-4 py-2 text-right font-mono font-medium ${it.overdue ? 'text-red-600' : ''}`}>
                  {formatAmount(it.openAmount)}
                </td>
                <td className="px-4 py-2 text-right font-mono text-slate-400">
                  {it.paidAmount ? formatAmount(it.paidAmount) : '—'}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <span className="font-mono text-slate-600">{it.agingDays}天</span>
                  {it.overdue && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">超期</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** 资产负债表科目行（可钻取明细账） */
function BsRows({ rows, showOpening, onDrill }: {
  rows: BalanceSheetRow[];
  showOpening: boolean;
  onDrill: (d: { code: string; name: string }) => void;
}) {
  return (
    <>
      {rows.map((r) => (
        <tr key={r.accountCode} className="hover:bg-slate-50">
          <td className="px-4 py-2">
            <button
              onClick={() => onDrill({ code: r.accountCode, name: r.accountName })}
              className="text-blue-600 hover:underline"
              title="查看明细账"
            >
              <span className="font-mono text-xs">{r.accountCode}</span> {r.accountName} ↗
            </button>
          </td>
          {showOpening && (
            <td className="px-4 py-2 text-right font-mono text-slate-500">{formatAmount(r.openingBalance)}</td>
          )}
          <td className={`px-4 py-2 text-right font-mono ${r.closingBalance < 0 ? 'text-red-600' : ''}`}>
            {formatAmount(r.closingBalance)}
          </td>
        </tr>
      ))}
    </>
  );
}

function LedgerModal({ code, name, period, onClose }: { code: string; name: string; period: string; onClose: () => void }) {
  const [data, setData] = useState<AccountLedger | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);

  const doPrint = async () => {
    setPrinting(true);
    try {
      await reportsApi.printLedger(code, period || undefined);
    } finally {
      setPrinting(false);
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      await reportsApi.exportLedger(code, period || undefined);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    reportsApi.ledger(code, period || undefined)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e.message || '加载失败'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code, period]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-lg w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-slate-800">明细账 · <span className="font-mono">{code}</span> {name}
            <span className="ml-2 text-xs font-normal text-slate-400">期间：{period || '全部'}</span>
          </h3>
          <div className="flex items-center gap-3">
            <ExportMenu
              busy={exporting || printing}
              items={[
                { label: '导出明细账（xlsx）', icon: '📊', onSelect: doExport, disabled: !data || exporting },
                { label: '套打明细账（A4）', icon: '🖨', onSelect: doPrint, disabled: !data || printing, hint: 'A4 套打页眉页脚，表头跳页重复' },
              ]}
            />
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
          </div>
        </div>
        <div className="overflow-auto px-5 py-3">
          {error && <div className="mb-2"><ErrorBanner message={error} /></div>}
          {loading && (
            <div className="py-8 text-center"><SkeletonRows colSpan={6} rows={4} /></div>
          )}
          {data && !loading && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left sticky top-0">
                <tr>
                  <th className="px-3 py-2">日期</th>
                  <th className="px-3 py-2">凭证号</th>
                  <th className="px-3 py-2">摘要</th>
                  <th className="px-3 py-2 text-right">借方</th>
                  <th className="px-3 py-2 text-right">贷方</th>
                  <th className="px-3 py-2 text-right">余额</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr className="text-slate-500 bg-slate-50/60">
                  <td className="px-3 py-2" colSpan={5}>期初余额</td>
                  <td className="px-3 py-2 text-right font-mono">{formatAmount(data.openingBalance)}</td>
                </tr>
                {data.rows.map((r) => (
                  <tr key={r.entryId + r.reference} className="hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap">{r.entryDate}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.reference}</td>
                    <td className="px-3 py-2 text-slate-600">{r.lineDescription || r.description || '-'}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.debit ? formatAmount(r.debit) : ''}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.credit ? formatAmount(r.credit) : ''}</td>
                    <td className={`px-3 py-2 text-right font-mono ${r.balance < 0 ? 'text-red-600' : ''}`}>{formatAmount(r.balance)}</td>
                  </tr>
                ))}
                {data.rows.length === 0 && (
                  <TableEmpty colSpan={6} icon="📒" title="该科目当期无已过账明细" hint="尝试切换期间，或确认相关凭证已过账" />
                )}
              </tbody>
              <tfoot className="bg-slate-50 font-medium">
                <tr>
                  <td className="px-3 py-2" colSpan={3}>本期合计</td>
                  <td className="px-3 py-2 text-right font-mono">{formatAmount(data.totalDebit)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatAmount(data.totalCredit)}</td>
                  <td className="px-3 py-2 text-right font-mono" title="期末余额">{formatAmount(data.closingBalance)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
